/**
 * "บล็อกผู้ส่งนี้" state of web chats (docs/webchat.md "บล็อกผู้ส่ง"): the card's block section per conversation and
 * the list in ตั้งค่า › แชทบนเว็บ. The server decides who may do what (api/app/webchat/blocks.py) and says so in `can`.
 * Realtime: a block/unblock on any screen reloads the web chat card (webchat.context on the socket) → the card calls
 * `load` again; the inbox row chip comes with the conversation itself (ConversationSummary.web_block).
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ApiError } from '../api/client'
import { getWcBlockApi } from '../api/wcblock'
import type { BlockBody, WebBlockList, WebBlockState } from '../api/wcblock/types'
import { useInbox } from './inbox'

const errText = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback)

export const useWcBlock = defineStore('wcblock', () => {
  /** conversation id → state (null = not a web chat). */
  const states = ref<Record<string, WebBlockState | null>>({})
  const loading = ref<Record<string, boolean>>({})
  const busy = ref(false)
  const list = ref<WebBlockList | null>(null)
  const listLoading = ref(false)
  const listError = ref<string | null>(null)

  async function load(convId: string): Promise<void> {
    if (loading.value[convId]) return
    loading.value[convId] = true
    try {
      states.value[convId] = await getWcBlockApi().getState(convId)
    } catch {
      /* keep what we had; the card's next refresh tries again */
    } finally {
      loading.value[convId] = false
    }
  }

  /** Block the visitor of this chat. Returns an error text (Thai) or null when done. */
  async function block(convId: string, body: BlockBody): Promise<string | null> {
    if (busy.value) return null
    busy.value = true
    try {
      states.value[convId] = await getWcBlockApi().block(convId, body)
      useInbox().flash('บล็อกผู้ส่งนี้แล้ว')
      return null
    } catch (e) {
      return errText(e, 'บล็อกไม่สำเร็จ ลองใหม่อีกครั้ง')
    } finally {
      busy.value = false
    }
  }

  async function unblock(convId: string, note: string | null): Promise<string | null> {
    if (busy.value) return null
    busy.value = true
    try {
      states.value[convId] = await getWcBlockApi().unblock(convId, note)
      useInbox().flash('ยกเลิกการบล็อกแล้ว ผู้ส่งนี้ส่งข้อความได้ตามปกติ')
      return null
    } catch (e) {
      return errText(e, 'ยกเลิกการบล็อกไม่สำเร็จ ลองใหม่อีกครั้ง')
    } finally {
      busy.value = false
    }
  }

  async function loadList(): Promise<void> {
    listLoading.value = true
    try {
      list.value = await getWcBlockApi().list()
      listError.value = null
    } catch (e) {
      listError.value = errText(e, 'โหลดรายชื่อผู้ส่งที่ถูกบล็อกไม่สำเร็จ')
    } finally {
      listLoading.value = false
    }
  }

  async function unblockFromList(blockId: string, note: string | null): Promise<string | null> {
    if (busy.value) return null
    busy.value = true
    try {
      list.value = await getWcBlockApi().unblockById(blockId, note)
      useInbox().flash('ยกเลิกการบล็อกแล้ว')
      return null
    } catch (e) {
      return errText(e, 'ยกเลิกการบล็อกไม่สำเร็จ ลองใหม่อีกครั้ง')
    } finally {
      busy.value = false
    }
  }

  return { states, loading, busy, load, block, unblock, list, listLoading, listError, loadList, unblockFromList }
})
